export const SUBSCRIPTION_LIFECYCLES = Object.freeze({
  ACTIVE: 'active',
  CANCELLATION_SCHEDULED: 'cancellation_scheduled',
  PAST_DUE: 'past_due',
  FREE: 'free',
  EXPIRED: 'expired',
  UNKNOWN: 'unknown',
});

export const normalizeSubscriptionStatus = (status) => {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'TRIALING') return 'ACTIVE';
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return 'EXPIRED';
  return ['ACTIVE', 'FREE', 'EXPIRED', 'PAST_DUE'].includes(normalized) ? normalized : 'UNKNOWN';
};

export const parseBillingDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  let parsedValue = value;
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
    parsedValue = numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue;
  }

  const date = new Date(parsedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatBillingDate = (value, options = {}) => {
  const date = parseBillingDate(value);
  if (!date) return options.fallback || 'Not available';
  return date.toLocaleDateString(options.locale || 'en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const normalizeSubscription = (subscription = {}, now = Date.now()) => {
  const status = normalizeSubscriptionStatus(subscription.subscription_status || subscription.status);
  const cancelAtPeriodEnd = Boolean(subscription.cancel_at_period_end);
  const isCancelled = Boolean(subscription.is_cancelled);
  const cancellationScheduled = status === 'ACTIVE' && (cancelAtPeriodEnd || isCancelled);

  let lifecycle = SUBSCRIPTION_LIFECYCLES.UNKNOWN;
  if (cancellationScheduled) lifecycle = SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED;
  else if (status === 'ACTIVE') lifecycle = SUBSCRIPTION_LIFECYCLES.ACTIVE;
  else if (status === 'PAST_DUE') lifecycle = SUBSCRIPTION_LIFECYCLES.PAST_DUE;
  else if (status === 'FREE') lifecycle = SUBSCRIPTION_LIFECYCLES.FREE;
  else if (status === 'EXPIRED') lifecycle = SUBSCRIPTION_LIFECYCLES.EXPIRED;

  const nextBillingValue = subscription.next_billing_date || subscription.renewal_date || '';
  const cancellationValue = subscription.cancellation_effective_date || (cancellationScheduled ? nextBillingValue : '');
  const passExpiryDate = parseBillingDate(subscription.pass_expiry_date);
  const nowTimestamp = now instanceof Date ? now.getTime() : Number(now);

  return {
    raw: subscription,
    planId: subscription.plan_id || subscription.plan_variant || '',
    planVariant: subscription.plan_variant || subscription.plan_id || '',
    planName: subscription.plan_name || subscription.current_plan || 'No Plan',
    planType: String(subscription.plan_type || '').toUpperCase(),
    frequency: String(subscription.frequency || subscription.billing_cycle || 'monthly').toLowerCase() === 'yearly'
      ? 'annual'
      : String(subscription.frequency || subscription.billing_cycle || 'monthly').toLowerCase(),
    priceAmount: Number(subscription.price_amount) || 0,
    currency: subscription.currency || 'INR',
    monthlyCredits: Number(subscription.monthly_credits) || 0,
    currentTryonBalance: Number(subscription.current_tryon_balance) || 0,
    status,
    lifecycle,
    cancelAtPeriodEnd,
    isCancelled,
    nextBillingDate: parseBillingDate(nextBillingValue),
    cancellationEffectiveDate: parseBillingDate(cancellationValue),
    nextCreditResetDate: parseBillingDate(subscription.next_credit_reset_date),
    passExpiryDate,
    hasActivePass: Boolean(passExpiryDate && Number.isFinite(nowTimestamp) && passExpiryDate.getTime() > nowTimestamp),
    canCancel: lifecycle === SUBSCRIPTION_LIFECYCLES.ACTIVE,
    canChangePlan: [
      SUBSCRIPTION_LIFECYCLES.ACTIVE,
      SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED,
      SUBSCRIPTION_LIFECYCLES.FREE,
      SUBSCRIPTION_LIFECYCLES.EXPIRED,
    ].includes(lifecycle),
  };
};

export const getSubscriptionDatePresentation = (subscription) => {
  if (subscription.lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED) {
    return { label: 'Access ends on', date: subscription.cancellationEffectiveDate || subscription.nextBillingDate };
  }
  if (subscription.lifecycle === SUBSCRIPTION_LIFECYCLES.ACTIVE) {
    return { label: 'Next billing date', date: subscription.nextBillingDate };
  }
  if (subscription.lifecycle === SUBSCRIPTION_LIFECYCLES.PAST_DUE) {
    return { label: 'Payment due', date: subscription.nextBillingDate };
  }
  return { label: 'Billing date', date: null };
};

export const hasPaidPlanAccess = (subscription) => [
  SUBSCRIPTION_LIFECYCLES.ACTIVE,
  SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED,
  SUBSCRIPTION_LIFECYCLES.PAST_DUE,
].includes(subscription?.lifecycle);
